import {BarChart2, Book, Calendar, FileText, HelpCircle, Home, X} from 'lucide-react'
import React from 'react'
import {useIntl} from 'react-intl'

import {MenuItem} from '../config/menuConfig'

interface MenuBubblesProps {
  options: MenuItem[]
  onOptionClick: (item: MenuItem) => void
  loading?: boolean
  showBack?: boolean
  onBack?: () => void
  showHide?: boolean
  onHideMenu?: () => void
}

const iconFor = (id: string) => {
  if (id.startsWith('help')) return <HelpCircle size={18} />
  if (id === 'faq') return <Book size={18} />
  if (id.includes('attendance')) return <Calendar size={18} />
  if (id.includes('stats')) return <BarChart2 size={18} />
  if (
    id.includes('manual') ||
    id.includes('how-to') ||
    id.includes('walkthrough') ||
    id.includes('faq')
  )
    return <FileText size={18} />
  return <Home size={18} />
}

const MenuBubbles: React.FC<MenuBubblesProps> = ({
  options,
  onOptionClick,
  loading,
  showBack,
  onBack,
  showHide,
  onHideMenu,
}) => {
  const intl = useIntl()
  const ITEM_HEIGHT = 72
  const GAP = 8
  const MAX_VISIBLE_ROWS = 2
  const maxHeight = MAX_VISIBLE_ROWS * ITEM_HEIGHT + GAP

  return (
    <div
      role='list'
      aria-label='Chatbot menu options'
      style={{
        maxHeight,
        overflowY: options.length > 4 ? 'auto' : 'visible',
        paddingRight: 6,
      }}
    >
      <div className='d-flex flex-wrap' style={{gap: 8}}>
        {showBack && (
          <button
            key={'__back__'}
            className='d-flex align-items-center'
            onClick={() => onBack && onBack()}
            title='Back to main menu'
            role='listitem'
            aria-pressed='false'
            style={{
              flex: '0 0 48%',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              borderRadius: 10,
              border: '1px solid var(--bs-border-color, #e9ecef)',
              backgroundColor: 'var(--bs-app-card-bg, var(--bs-body-bg, #fff))',
              color: 'var(--bs-body-color, #212529)',
              textAlign: 'left',
              cursor: 'pointer',
              boxShadow: 'var(--bs-shadow-sm, none)',
              minHeight: ITEM_HEIGHT - 8,
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
                background: 'transparent',
                color: 'var(--bs-primary, #0d6efd)',
              }}
            >
              <Home size={18} />
            </div>
            <div style={{flex: 1}}>
              <div style={{fontWeight: 600}}>
                {intl.formatMessage({
                  id: 'CHATBOT.MENU.BACK_TO_MAIN',
                })}
              </div>
            </div>
          </button>
        )}
        {showHide && (
          <button
            key={'__hide__'}
            className='d-flex align-items-center'
            onClick={() => onHideMenu && onHideMenu()}
            title='Hide menu'
            role='listitem'
            aria-pressed='false'
            style={{
              flex: '0 0 48%',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              borderRadius: 10,
              border: '1px solid var(--bs-border-color, #e9ecef)',
              backgroundColor: 'var(--bs-app-card-bg, var(--bs-body-bg, #fff))',
              color: 'var(--bs-body-color, #212529)',
              textAlign: 'left',
              cursor: 'pointer',
              boxShadow: 'var(--bs-shadow-sm, none)',
              minHeight: ITEM_HEIGHT - 8,
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
                background: 'transparent',
                color: 'var(--bs-danger, #dc3545)',
              }}
            >
              <X size={18} />
            </div>
            <div style={{flex: 1}}>
              <div style={{fontWeight: 600}}>
                {intl.formatMessage({
                  id: 'CHATBOT.MENU.HIDE_MENU',
                })}
              </div>
            </div>
          </button>
        )}
        {options.map((option) => (
          <button
            key={option.id}
            className='d-flex align-items-center'
            onClick={() => onOptionClick(option)}
            disabled={!!loading}
            title={option.description}
            role='listitem'
            aria-pressed='false'
            style={{
              flex: '0 0 48%',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              borderRadius: 10,
              border: '1px solid var(--bs-border-color, #e9ecef)',
              backgroundColor: 'var(--bs-app-card-bg, var(--bs-body-bg, #fff))',
              color: 'var(--bs-body-color, #212529)',
              textAlign: 'left',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: 'var(--bs-shadow-sm, none)',
              minHeight: ITEM_HEIGHT - 8,
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
                background: 'transparent',
                color: 'var(--bs-primary, #0d6efd)',
              }}
            >
              {iconFor(option.id)}
            </div>
            <div style={{flex: 1}}>
              <div style={{fontWeight: 600}}>
                {option.labelId
                  ? intl.formatMessage({
                      id: option.labelId,
                    })
                  : option.label}
              </div>
              {(option.description || option.descriptionId) && (
                <div
                  style={{
                    fontSize: '0.8rem',
                    color: 'var(--bs-secondary-color, #6c757d)',
                  }}
                >
                  {option.descriptionId
                    ? intl.formatMessage({
                        id: option.descriptionId,
                      })
                    : option.description}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

export default MenuBubbles
